import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, StarIcon, MonitorIcon, CodeIcon, ServerIcon, SearchCheckIcon, UserIcon, BuildingIcon, MapPinIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from './FinalCtaSection';
import { useSeo } from './useSeo';
interface NearbyLocation {
  name: string;
  slug: string;
}
interface FaqItem {
  q: string;
  a: string;
}
interface LocalPageProps {
  cityName: string;
  region: string;
  introText: string;
  localContext: string;
  nearbyLocations: NearbyLocation[];
  faqs: FaqItem[];
}
export function LocalPageTemplate({
  cityName,
  region,
  introText,
  localContext,
  nearbyLocations,
  faqs
}: LocalPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const desc = `Professioneel webdesign in ${cityName}. ${introText.length > 120 ? introText.substring(0, 120) + '...' : introText}`;
  useSeo({
    title: `Webdesign ${cityName} | Professioneel webdesign | Noord`,
    description: desc,
    canonical: `https://noorddigital.nl/webdesign-${cityName.toLowerCase().replace(/\s+/g, '-')}`,
    jsonLd: [{
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Noord Digital',
      description: `Professioneel webdesign en SEO voor financiële dienstverleners in ${cityName}. Conversiegerichte websites die resultaat opleveren.`,
      url: `https://noorddigital.nl/webdesign-${cityName.toLowerCase().replace(/\s+/g, '-')}`,
      telephone: '+31616265214',
      email: 'info@noorddigital.nl',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Schuit 8a',
        addressLocality: 'Oudkarspel',
        postalCode: '1724 BD',
        addressCountry: 'NL'
      },
      areaServed: {
        '@type': 'City',
        name: cityName
      },
      priceRange: '€€',
      sameAs: ['https://facebook.com', 'https://instagram.com', 'https://linkedin.com']
    }, ...(faqs.length > 0 ? [{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    }] : [])]
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cityName]);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <main className="pt-16 lg:pt-[72px]" data-id="element-726" data-mp="3n7k2a776gxvw">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-727" data-mp="3i9sb5fecxfsr">
        <div className="absolute inset-0 pointer-events-none" data-id="element-728" data-mp="1xs9ca1h0e121">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-729" data-mp="1xqpzikuka850" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-730" data-mp="1numsp1orllbq" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-731" data-mp="1suhnhgbnuv22" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-732" data-mp="27j40hbdtfaq4">
          <p className="text-accent font-semibold text-sm tracking-widest uppercase mb-4 animate-fade-in-up" data-id="element-733" data-mp="2bjo0qntoqugh">
            {region}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-734" data-mp="w1m6v4py7rhb">
            Webdesign {cityName}
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-735" data-mp="1gcf9hs2k7vvb">
            {introText}
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-736" data-mp="1bjvtkogegonc">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-737" data-mp="16lc78mxw9aug">
              Plan een gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-738" data-mp="1uc7tl4jkzdub" />
            </a>
          </div>
        </div>
      </section>

      {/* Lokale context */}
      <section className="py-24 bg-surface-muted" data-id="element-739" data-mp="1lb08jo62s9i6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-740" data-mp="1la8fajzyanv2">
          <div className="text-center mb-16" data-id="element-741" data-mp="dxy8cjamy57p">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-742" data-mp="dyri7t4w5au2">
              Waarom een goede website in {cityName} onmisbaar is
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto" data-id="element-743" data-mp="2ljzp16hsq9tu">
              {localContext}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-744" data-mp="2plbjbpbm7j8d">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-745" data-mp="2ujmnpkj551j7">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-746" data-mp="2zal92yizkptb">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-747" data-mp="349y4i7nxb57i" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-748" data-mp="398r8n9hbljv0">
                Meer klanten uit {cityName}
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-749" data-mp="3da4evkobrp2g">
                Zet websitebezoekers om in betalende klanten met een
                conversiegerichte website die aansluit bij de lokale markt.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-750" data-mp="1iy643eadal4g">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-751" data-mp="1nwhdxp0nwvmo">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-752" data-mp="2r752c9gq96gy" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-753" data-mp="2m80v3odd5it9">
                Lokaal vindbaar in Google
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-754" data-mp="308lbkdvt32jl">
                Word gevonden door potentiële klanten uit {cityName} en
                omstreken die zoeken naar jouw diensten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-755" data-mp="3a2v3jugu5yts">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-756" data-mp="355li0pwki7b8">
                <StarIcon className="w-6 h-6 text-accent" data-id="element-757" data-mp="3iwqb195fk1cl" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-758" data-mp="3ex7pqc9p5o98">
                Professionele uitstraling
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-759" data-mp="3stl7lmi57ro8">
                Maak een sterke eerste indruk met een modern webdesign dat
                vertrouwen wekt bij klanten in de regio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onze diensten */}
      <section className="py-24 bg-white" data-id="element-760" data-mp="ga0o82b5yw4n">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-761" data-mp="1fcbq47cixfj">
          <div className="text-center mb-16" data-id="element-762" data-mp="1h6vhf6vsji1">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-763" data-mp="6cw0vz5l0um9">
              Onze diensten in {cityName}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-764" data-mp="3optttm3yin6x">
              Alles wat je nodig hebt voor online succes, onder één dak.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-765" data-mp="3ev1twbmwjo8z">
            <a href="#/website-laten-maken" className="group border border-navy-100 rounded-card p-6 hover:shadow-medium hover:border-accent/20 transition-all duration-300" data-id="element-766" data-mp="3js0m2on09l4j">
              <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-accent-light flex items-center justify-center mb-4 transition-colors duration-300" data-id="element-767" data-mp="39tcl72n5jmo2">
                <MonitorIcon className="w-6 h-6 text-navy-700 group-hover:text-accent transition-colors duration-300" data-id="element-768" data-mp="3lbbu4njkkp4o" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2" data-id="element-769" data-mp="3gao5u9mq6me5">
                Website laten maken
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-770" data-mp="3beyyvl8dm37t">
                Conversiegerichte websites die bezoekers omzetten in klanten.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-771" data-mp="32jssol571j4r">
                Meer info
                <ArrowRightIcon className="w-4 h-4" data-id="element-772" data-mp="2smwr6zdppx9p" />
              </span>
            </a>

            <a href="#/seo-voor-websites" className="group border border-navy-100 rounded-card p-6 hover:shadow-medium hover:border-accent/20 transition-all duration-300" data-id="element-773" data-mp="2nn20owxun9p6">
              <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-accent-light flex items-center justify-center mb-4 transition-colors duration-300" data-id="element-774" data-mp="z479v6u5mxn3">
                <SearchCheckIcon className="w-6 h-6 text-navy-700 group-hover:text-accent transition-colors duration-300" data-id="element-775" data-mp="1373wr9qq9fwu" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2" data-id="element-776" data-mp="1d3p441q7z33u">
                SEO optimalisatie
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-777" data-mp="fh9l2vi59xe6">
                Hoger scoren in Google en meer organisch verkeer genereren.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-778" data-mp="pdcp66wk463r">
                Meer info
                <ArrowRightIcon className="w-4 h-4" data-id="element-779" data-mp="u6e4uh4l89oe" />
              </span>
            </a>

            <a href="#/wordpress-websites" className="group border border-navy-100 rounded-card p-6 hover:shadow-medium hover:border-accent/20 transition-all duration-300" data-id="element-780" data-mp="3ssh0s25wxuv1">
              <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-accent-light flex items-center justify-center mb-4 transition-colors duration-300" data-id="element-781" data-mp="2m7uygo874lbe">
                <CodeIcon className="w-6 h-6 text-navy-700 group-hover:text-accent transition-colors duration-300" data-id="element-782" data-mp="2w47onc4j8h1e" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2" data-id="element-783" data-mp="310etf1l6zmhr">
                WordPress websites
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-784" data-mp="28dkeslb23hc7">
                Flexibel en makkelijk te beheren met het populairste CMS.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-785" data-mp="2dbc55aw1yvea">
                Meer info
                <ArrowRightIcon className="w-4 h-4" data-id="element-786" data-mp="1knq3472h3ytj" />
              </span>
            </a>

            <a href="#/hosting-onderhoud" className="group border border-navy-100 rounded-card p-6 hover:shadow-medium hover:border-accent/20 transition-all duration-300" data-id="element-787" data-mp="1pmkkr5opfhc2">
              <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-accent-light flex items-center justify-center mb-4 transition-colors duration-300" data-id="element-788" data-mp="2re9xr29xygi">
                <ServerIcon className="w-6 h-6 text-navy-700 group-hover:text-accent transition-colors duration-300" data-id="element-789" data-mp="3oymbeia2pl79" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2" data-id="element-790" data-mp="3k0kplpl9j7s3">
                Hosting & onderhoud
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-791" data-mp="3f6gjt9nqh5sl">
                Snel, veilig en altijd up-to-date. Wij regelen het.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-792" data-mp="368wf1qqnwrd8">
                Meer info
                <ArrowRightIcon className="w-4 h-4" data-id="element-793" data-mp="2w9p5zfqynivx" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Proces */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-794" data-mp="2mki4wad3o2ja">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-795" data-mp="2rlxpk1bd8715" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-796" data-mp="35caxw4s65nr0">
          <div className="text-center mb-16" data-id="element-797" data-mp="35d2sbca15ffp">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-798" data-mp="3abxb4e7zz3zt">
              In 4 stappen naar jouw website
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-799" data-mp="2wjhfhyhfe88x">
              Van kennismaking tot livegang — wij ontzorgen je volledig.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-800" data-mp="3oax39vupy10q">
            {[{
            step: 'Intake',
            desc: 'We bespreken jouw wensen, doelgroep en doelen.'
          }, {
            step: 'Ontwerp',
            desc: 'Een op maat gemaakt design dat past bij jouw merk.'
          }, {
            step: 'Bouw',
            desc: 'Technisch perfect gebouwd, snel en SEO-klaar.'
          }, {
            step: 'Live',
            desc: 'Jouw website gaat live met onderhoud en support.'
          }].map((item, i) => <div key={item.step} className="bg-white/[0.06] border border-white/10 p-6 rounded-2xl text-center relative" data-id="element-801" data-mp="toz538uyy1rp" data-mpi={i}>
                <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-4 font-bold" data-id="element-802" data-mp="18k59jref1six">
                  {i + 1}
                </div>
                <h3 className="text-white font-bold mb-2" data-id="element-803" data-mp="2jztxakzhuvks">{item.step}</h3>
                <p className="text-navy-300 text-sm" data-id="element-804" data-mp="2f18wajcf47va">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-24 bg-surface-muted" data-id="element-805" data-mp="tk01uorvhezp">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-806" data-mp="tktajvae6ekh">
          <div className="text-center mb-16" data-id="element-807" data-mp="xlllqu2tqtje">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-808" data-mp="xkk0jws3gqny">
              Voor wie wij bouwen in {cityName}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-809" data-mp="17izq3vbh3rvc">
            <div className="bg-white p-8 rounded-card shadow-soft text-center" data-id="element-810" data-mp="1cj5jbfxe5mfj">
              <div className="w-12 h-12 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-4" data-id="element-811" data-mp="1cgsuqjx4odti">
                <UserIcon className="w-6 h-6 text-accent" data-id="element-812" data-mp="1lbfuazg41vxu" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-813" data-mp="1ldk23sh4rf3e">ZZP'ers</h3>
              <p className="text-slate-600" data-id="element-814" data-mp="1qc3k5hsls6qh">
                Professionele websites voor zelfstandigen die een sterke online
                start willen maken.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft text-center" data-id="element-815" data-mp="25f526g4wahld">
              <div className="w-12 h-12 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-4" data-id="element-816" data-mp="20dfsp4xbmbze">
                <BuildingIcon className="w-6 h-6 text-accent" data-id="element-817" data-mp="1vfw9rrpohb14" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-818" data-mp="1rf4232wgfe6y">
                MKB bedrijven
              </h3>
              <p className="text-slate-600" data-id="element-819" data-mp="2o3jpasc3dbgw">
                Uitgebreide websites en maatwerk oplossingen voor groeiende
                bedrijven in de regio.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft text-center" data-id="element-820" data-mp="2ecp6xz1yuw0q">
              <div className="w-12 h-12 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-4" data-id="element-821" data-mp="2ablt1i3hba25">
                <MapPinIcon className="w-6 h-6 text-accent" data-id="element-822" data-mp="36w45paalto3t" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-823" data-mp="322rrzv7gr1pu">
                Lokale ondernemers
              </h3>
              <p className="text-slate-600" data-id="element-824" data-mp="1072i7hsuej3u">
                Lokale vindbaarheid en conversie voor winkels, horeca en
                dienstverleners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && <section className="py-24 bg-white" data-id="element-825" data-mp="ve9duv1adcsa">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-826" data-mp="vd7qps6zwzu7">
            <div className="text-center mb-16" data-id="element-827" data-mp="he838nv7yqzm">
              <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-828" data-mp="memd8bgdsb0a">
                Veelgestelde vragen over webdesign in {cityName}
              </h2>
            </div>

            <div className="space-y-4" data-id="element-829" data-mp="cgq1pijj7e1n">
              {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-830" data-mp="cisrq2kop76d" data-mpi={index}>
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-831" data-mp="26u7goh5vf2l2">
                    <span className="font-bold text-navy-900 pr-4" data-id="element-832" data-mp="3mfi3wro8d69d">
                      {faq.q}
                    </span>
                    {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-833" data-mp="5fi08xf3yp7" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-834" data-mp="dwkfb4dbcewd" />}
                  </button>
                  {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-835" data-mp="nv0430ovbc2p">
                      {faq.a}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>}

      {/* Silo footer — Nearby locations + internal links */}
      <section className="py-16 bg-navy-50 border-y border-navy-100" data-id="element-836" data-mp="2kpms5077ikca">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-837" data-mp="1yvp7v2o128gr">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-838" data-mp="1ytbf50gtl8cd">
            {/* Nearby locations */}
            <div data-id="element-839" data-mp="23rw4u5yg49mk">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-840" data-mp="28l8u8iay8kgd">
                Webdesign in de buurt
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-841" data-mp="2djaakvgzq5o5">
                {nearbyLocations.map(loc => <a key={loc.slug} href={`#/webdesign-${loc.slug}`} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-842" data-mp="1fy4g9ntvovsg">
                    Webdesign {loc.name}
                  </a>)}
              </nav>
            </div>

            {/* Services */}
            <div data-id="element-843" data-mp="318l3jqs3mvrq">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-844" data-mp="3194l36du7i58">
                Onze diensten
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-845" data-mp="uldxv3qbb2rm">
                <a href="#/website-laten-maken" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-846" data-mp="ukuinus1qsha">
                  Professionele website laten maken
                </a>
                <a href="#/seo-voor-websites" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-847" data-mp="fwhmoxsyl9ms">
                  SEO optimalisatie
                </a>
                <a href="#/wordpress-websites" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-848" data-mp="6vjnqtxdnsb2">
                  WordPress website bouwen
                </a>
                <a href="#/hosting-onderhoud" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-849" data-mp="3t4udo0qol04f">
                  Hosting en technisch beheer
                </a>
              </nav>
            </div>

            {/* Kennisbank */}
            <div data-id="element-850" data-mp="2j4xu6hyl4o6h">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-851" data-mp="2j7k0yfdniznn">
                Kennisbank
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-852" data-mp="29f4nnvdauz93">
                <a href="#/kosten-website-laten-maken" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-853" data-mp="29djottjycu5f">
                  Wat kost een website?
                </a>
                <a href="#/website-laten-maken-checklist" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-854" data-mp="1vfvf9hvc59fo">
                  Website checklist
                </a>
                <a href="#/wordpress-vs-wix" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-855" data-mp="1ljs45miia0gl">
                  WordPress of Wix kiezen?
                </a>
                <a href="#/" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-856" data-mp="3ocz0njhszisz">
                  Terug naar homepage
                </a>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-857" data-mp="5zj2wbipa3os" />
    </main>;
}